import React, { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faStar, faChartBar, faBullseye, faSoccerBall, faPersonRunning, faAward, faHandshake, faGamepad, faChartLine, faTrophy, faArrowUp, faArrowDown, faCompress, faExpand } from '@fortawesome/free-solid-svg-icons';
import { Player, DisplayFixture } from '../../../domain/entities';
import { calculateStarRating } from '../../../domain/constants';
import { calculateMatchPerformance } from '../../../utils/utilities';
import { usePanelViewPreferences } from '../../../hooks/usePanelViewPreferences';
import { CollapsiblePanel } from '../../shared/CollapsiblePanel';
import './PlayerStatsPanel.css';

interface StatDetail {
    icon: IconDefinition;
    label: string;
    description: string;
    value: string | number;
}

interface PlayerStatsPanelProps {
    player: Player;
    leagueAverageSkill: number;
    leagueName: string;
    last5Fixtures: DisplayFixture[];
    teamName: string;
    onSkillClick?: () => void;
    onPerformanceClick?: () => void;
}

interface StatCategory {
    icon: IconDefinition;
    label: string;
    shortLabel: string;
    description: string;
    value: string | number;
    type?: 'default' | 'positive' | 'warning' | 'error';
}

const getFormColor = (form: number): 'positive' | 'warning' | 'error' | 'default' => {
    if (form >= 5) return 'positive';
    if (form >= 0) return 'default';
    if (form >= -5) return 'warning';
    return 'error';
};

// Helper function to generate className strings with conditional compact mode
const getClassNames = (baseClass: string, isCompact: boolean, compactClass?: string): string => {
    const compact = compactClass || `${baseClass}-compact`;
    return isCompact ? `${baseClass} ${compact}` : baseClass;
};

const StatItem: React.FC<{ icon: IconDefinition; shortLabel: string; value: string | number; type?: 'default' | 'positive' | 'warning' | 'error'; onClick?: () => void; fullLabel?: string; isCompactView?: boolean }> = ({ icon, shortLabel, value, type = 'default', onClick, fullLabel, isCompactView = false }) => (
    <div className={`stat-item stat-item-${type} ${onClick ? 'stat-item-clickable' : ''} ${isCompactView ? 'stat-item-compact' : ''}`} onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default' }} title={fullLabel || shortLabel}>
        <div className="stat-item-icon"><FontAwesomeIcon icon={icon} /></div>
        <div className={`stat-item-content ${isCompactView ? 'stat-item-content-compact' : ''}`}>
            <div className="stat-item-value">{value}</div>
            <div className="stat-item-label">{shortLabel}</div>
        </div>
    </div>
);

export const PlayerStatsPanel: React.FC<PlayerStatsPanelProps> = ({ player, leagueAverageSkill, leagueName, last5Fixtures, teamName, onSkillClick, onPerformanceClick }) => {
    const [selectedStatDetail, setSelectedStatDetail] = useState<StatDetail | null>(null);
    const { isCompact, toggleView } = usePanelViewPreferences();
    const isCompactView = isCompact('playerStats');
    const baseEffectiveSkill = player.skill + player.form;
    const expectedMatchPerformance = calculateMatchPerformance(player);
    const rating = calculateStarRating(baseEffectiveSkill, leagueAverageSkill);

    // Calculate last 5 games stats
    const last5Stats = useMemo(() => {
        if (last5Fixtures.length === 0) {
            return {
                avgRating: '--',
                avgForm: '--',
                formTrend: '',
                gamesPlayed: 0
            };
        }

        // Extract player ratings from fixture results
        const ratings = last5Fixtures
            .map(f => {
                if (!f.result) return 0;
                // Determine if player was home or away
                const isHome = f.home === teamName;
                const playerRatingsDict = isHome ? f.result.homePlayerRatings : f.result.awayPlayerRatings;
                return playerRatingsDict?.[player.id] ?? 0;
            })
            .filter(r => r > 0);

        const avgRating = ratings.length > 0 ? (ratings.reduce((a, b) => a + b, 0) / ratings.length).toFixed(1) : '--';

        // Use player.recentRatings for form trend (recentRatings tracks actual match performance)
        // This is more reliable than trying to extract non-existent playerForm fields
        const recentRatings = player.recentRatings || [];
        const forms = recentRatings.length > 0 ? recentRatings : [0]; // Use recent ratings as proxy for form
        const avgForm = forms.length > 0 ? (forms.reduce((a, b) => a + b, 0) / forms.length).toFixed(1) : '--';

        // Determine form trend (positive if improving) - comparing first half vs second half of recent ratings
        const firstHalf = forms.slice(0, Math.floor(forms.length / 2)).reduce((a, b) => a + b, 0) / Math.max(1, Math.floor(forms.length / 2));
        const secondHalf = forms.slice(Math.floor(forms.length / 2)).reduce((a, b) => a + b, 0) / Math.max(1, forms.length - Math.floor(forms.length / 2));
        const formTrend = secondHalf > firstHalf ? '📈' : secondHalf < firstHalf ? '📉' : '➡️';

        return {
            avgRating,
            avgForm,
            formTrend,
            gamesPlayed: last5Fixtures.length
        };
    }, [last5Fixtures, player.id, player.recentRatings, teamName]);

    // Calculate season averages
    const seasonStats = useMemo(() => {
        const gamesPlayed = player.gamesPlayed || 0;
        const goalsScored = player.goalsScored || 0;
        const assists = player.assists || 0;
        const potmAwards = player.potmAwards || 0;

        const goalsPerGame = gamesPlayed > 0 ? (goalsScored / gamesPlayed).toFixed(2) : '0.00';
        const assistsPerGame = gamesPlayed > 0 ? (assists / gamesPlayed).toFixed(2) : '0.00';

        return { gamesPlayed, goalsScored, assists, potmAwards, goalsPerGame, assistsPerGame };
    }, [player.gamesPlayed, player.goalsScored, player.assists, player.potmAwards]);

    const careerGoals = useMemo(() => {
        const history = player.history || [];
        return history.reduce((sum, h) => sum + (h.goalsScored || 0), 0) + (player.goalsScored || 0);
    }, [player.history, player.goalsScored]);

    const careerMatches = useMemo(() => {
        const history = player.history || [];
        return history.reduce((sum, h) => sum + (h.gamesPlayed || 0), 0) + (player.gamesPlayed || 0);
    }, [player.history, player.gamesPlayed]);

    const statCategories = {
        overview: [
            { icon: faStar, label: 'Rating', shortLabel: 'Rating', description: 'Overall player rating relative to league average', value: `${rating.toFixed(1)}★`, type: 'default' as const },
            { icon: faBullseye, label: 'Base Skill', shortLabel: 'Base Skill', description: 'Raw skill level without form modifier', value: `${player.skill}`, type: 'default' as const },
            { icon: faChartBar, label: 'Expected Match Performance', shortLabel: 'Eff. Skill', description: 'Expected performance in next match (based on skill, form, fitness, and injury)', value: `${expectedMatchPerformance}`, type: getFormColor(player.form) },
            { icon: faChartBar, label: 'Form', shortLabel: 'Form', description: 'Current form modifier (-5 to +5)', value: `${player.form >= 0 ? '+' : ''}${player.form}`, type: getFormColor(player.form) },
        ],
        career: [
            { icon: faPersonRunning, label: 'Career Matches', shortLabel: 'Matches', description: 'Total matches played in career', value: careerMatches, type: 'default' as const },
            { icon: faSoccerBall, label: 'Goals', shortLabel: 'Goals', description: 'Total goals scored across all teams', value: careerGoals, type: 'default' as const },
            { icon: faHandshake, label: 'Assists', shortLabel: 'Assists', description: 'Total assists in career', value: (player.assists || 0) + (player.history?.reduce((sum, h) => sum + (h.assists || 0), 0) || 0), type: 'default' as const },
            { icon: faAward, label: 'POTM Awards', shortLabel: 'POTM', description: 'Player of the Match awards earned', value: (player.potmAwards || 0) + (player.history?.reduce((sum, h) => sum + (h.potmAwards || 0), 0) || 0), type: 'default' as const },
        ],
        season: [
            { icon: faPersonRunning, label: 'Season Matches', shortLabel: 'Played', description: 'Matches started this season', value: seasonStats.gamesPlayed, type: 'default' as const },
            { icon: faSoccerBall, label: 'Goals', shortLabel: 'Goals', description: 'Goals scored this season', value: seasonStats.goalsScored, type: seasonStats.goalsScored > 5 ? 'positive' : 'default' },
            { icon: faHandshake, label: 'Assists', shortLabel: 'Assists', description: 'Assists this season', value: seasonStats.assists, type: seasonStats.assists > 3 ? 'positive' : 'default' },
            { icon: faChartBar, label: 'Goals per Match', shortLabel: 'G/M', description: 'Average goals per match this season', value: seasonStats.goalsPerGame, type: seasonStats.goalsScored > 5 ? 'positive' : 'default' },
        ],
        recent: [
            { icon: faStar, label: 'Last 5 Avg Rating', shortLabel: 'Avg Rating', description: 'Average rating in last 5 matches', value: last5Stats.avgRating, type: 'default' as const },
            { icon: faChartBar, label: 'Last 5 Avg Form', shortLabel: 'Avg Form', description: 'Average form in last 5 matches', value: last5Stats.avgForm, type: 'default' as const },
            { icon: last5Stats.formTrend === '📈' ? faArrowUp : (last5Stats.formTrend === '📉' ? faArrowDown : faChartBar), label: 'Form Trend', shortLabel: 'Trend', description: 'Form improving, stable, or declining', value: `${last5Stats.formTrend}`, type: 'default' as const },
            { icon: faGamepad, label: 'Games in Last 5', shortLabel: 'Games (L5)', description: 'Matches played in last 5', value: last5Stats.gamesPlayed, type: 'default' as const },
        ],
    };

    return (
        <CollapsiblePanel title="Player Statistics" className="panel player-stats-panel" panelId={`player-stats-${player.id}`}>
            <div className="panel-content">
                <div style={{ position: 'relative', marginBottom: '12px' }}>
                    <button
                        onClick={() => toggleView('playerStats')}
                        style={{
                            position: 'absolute',
                            top: '0',
                            right: '0',
                            background: 'none',
                            border: 'none',
                            color: 'inherit',
                            cursor: 'pointer',
                            padding: '4px 8px',
                            opacity: 0.7,
                            transition: 'opacity 0.2s'
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
                        onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.7')}
                        title={isCompactView ? 'Show full view' : 'Show compact view'}
                    >
                        <FontAwesomeIcon icon={isCompactView ? faExpand : faCompress} />
                    </button>
                </div>
                <div className={getClassNames("stats-container", isCompactView)}>
                    {/* Overview Section */}
                    <div className={getClassNames("stats-section", isCompactView)}>
                        <h4 className="stats-section-title"><FontAwesomeIcon icon={faChartBar} style={{ marginRight: '8px' }} />Overview</h4>
                        <div className={getClassNames("stats-grid", isCompactView)}>
                            {statCategories.overview.map((stat, idx) => {
                                let onClick: (() => void) | undefined;
                                if (idx === 1 || idx === 2) onClick = onSkillClick; // Effective Skill or Base Skill
                                return <StatItem key={idx} icon={stat.icon} shortLabel={stat.shortLabel} value={stat.value} type={stat.type} onClick={onClick} fullLabel={stat.label} isCompactView={isCompactView} />;
                            })}
                        </div>
                    </div>

                    {/* Recent Form Section */}
                    {last5Fixtures.length > 0 && (
                        <div className={getClassNames("stats-section", isCompactView)}>
                            <h4 className="stats-section-title"><FontAwesomeIcon icon={faChartLine} style={{ marginRight: '8px' }} />Last 5 Games</h4>
                            <div className={getClassNames("stats-grid", isCompactView)}>
                                {statCategories.recent.map((stat, idx) => (
                                    <StatItem key={idx} icon={stat.icon} shortLabel={stat.shortLabel} value={stat.value} type={stat.type} onClick={() => setSelectedStatDetail({icon: stat.icon, label: stat.label, description: stat.description, value: stat.value})} fullLabel={stat.label} isCompactView={isCompactView} />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Season Stats Section */}
                    <div className={getClassNames("stats-section", isCompactView)}>
                        <h4 className="stats-section-title"><FontAwesomeIcon icon={faBullseye} style={{ marginRight: '8px' }} />Season Performance</h4>
                        <div className={getClassNames("stats-grid", isCompactView)}>
                            {statCategories.season.map((stat, idx) => (
                                <StatItem key={idx} icon={stat.icon} shortLabel={stat.shortLabel} value={stat.value} type={stat.type} onClick={() => setSelectedStatDetail({icon: stat.icon, label: stat.label, description: stat.description, value: stat.value})} fullLabel={stat.label} isCompactView={isCompactView} />
                            ))}
                        </div>
                    </div>


                    {/* Career Stats Section */}
                    <div className={getClassNames("stats-section", isCompactView)}>
                        <h4 className="stats-section-title"><FontAwesomeIcon icon={faTrophy} style={{ marginRight: '8px' }} />Career Statistics</h4>
                        <div className={getClassNames("stats-grid", isCompactView)}>
                            {statCategories.career.map((stat, idx) => (
                                <StatItem key={idx} icon={stat.icon} shortLabel={stat.shortLabel} value={stat.value} type={stat.type} onClick={() => setSelectedStatDetail({icon: stat.icon, label: stat.label, description: stat.description, value: stat.value})} fullLabel={stat.label} isCompactView={isCompactView} />
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Stat Detail Modal */}
            {selectedStatDetail && (
                <div className="stat-detail-modal-overlay" onClick={() => setSelectedStatDetail(null)}>
                    <div className="stat-detail-modal" onClick={(e) => e.stopPropagation()}>
                        <button className="stat-detail-modal-close" onClick={() => setSelectedStatDetail(null)}>✕</button>
                        <div className="stat-detail-modal-header">
                            <div className="stat-detail-modal-icon">{selectedStatDetail.icon}</div>
                            <div>
                                <h3>{selectedStatDetail.label}</h3>
                                <p className="stat-detail-modal-value">{selectedStatDetail.value}</p>
                            </div>
                        </div>
                        <p className="stat-detail-modal-description">{selectedStatDetail.description}</p>
                    </div>
                </div>
            )}
        </CollapsiblePanel>
    );
};
